import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Grid, Chip, Avatar, Divider,
  LinearProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton,
  Tooltip, useMediaQuery, alpha, CircularProgress,
  Button, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  HealthAndSafety as HealthIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import api from '../services/api';

// Função para parsear JSON com segurança
function safeJsonParse(value) {
  if (!value) return [];
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      if (value.includes(',') && !value.startsWith('[')) {
        return value.split(',').map(s => s.trim());
      }
      if (value.trim()) {
        return [value.trim()];
      }
      return [];
    }
  }
  return [];
}

function FichaTecnica() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);

  useEffect(() => {
    carregarFicha();
  }, [id]);

  const carregarFicha = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/computadores/${id}/ficha-tecnica`);
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar ficha técnica:', error);
      toast.error('Erro ao carregar dados do computador');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const token = localStorage.getItem('@Jobson:token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    window.open(`/api/computadores/${id}/print-ficha?token=${token}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      aberta: 'warning',
      em_andamento: 'info',
      finalizada: 'success',
      cancelada: 'error'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Layout title="Ficha Técnica">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!dados) {
    return (
      <Layout title="Ficha Técnica">
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Computador não encontrado</Typography>
          <Button onClick={() => navigate('/computadores')} sx={{ mt: 2 }}>
            Voltar
          </Button>
        </Card>
      </Layout>
    );
  }

  const { computador } = dados;
  const { saude, historico, estatisticas } = computador;

  return (
    <Layout title={`Ficha Técnica - ${computador.nome_identificacao}`}>
      {/* Botão Voltar */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/computadores')}
        sx={{ mb: 2 }}
      >
        Voltar para Computadores
      </Button>

      {/* Header */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ 
              width: 60, 
              height: 60, 
              bgcolor: alpha('#0A2647', 0.1),
              color: '#0A2647'
            }}>
              <ComputerIcon sx={{ fontSize: 35 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0A2647' }}>
                {computador.nome_identificacao}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {computador.cliente_nome} • {computador.marca} {computador.modelo}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={`Saúde: ${saude.indice}%`}
              sx={{
                bgcolor: saude.cor,
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                px: 2,
                py: 2
              }}
            />
            <Tooltip title="Imprimir Ficha Técnica">
              <IconButton 
                onClick={handlePrint}
                sx={{ 
                  color: '#0A2647',
                  '&:hover': { bgcolor: alpha('#0A2647', 0.08) }
                }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Card>

      {/* Cards de Saúde */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: saude.cor }}>
              {saude.status}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total de OS</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2647' }}>
              {saude.total_manutencoes}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Última Manutenção</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2647', fontSize: '0.9rem' }}>
              {formatDate(saude.ultima_manutencao)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Média entre Manutenções</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2647' }}>
              {saude.media_dias_entre_manutencoes} dias
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de Saúde */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
          Índice de Saúde
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={saude.indice}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: alpha('#0A2647', 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: saude.cor,
                  borderRadius: 6
                }
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: saude.cor, minWidth: 60 }}>
            {saude.indice}%
          </Typography>
        </Box>
      </Card>

      {/* Ficha Técnica */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
            💻 Ficha Técnica
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.cliente_nome || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Tipo</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                  {computador.tipo || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Marca</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.marca || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Modelo</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.modelo || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Número de Série</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.numero_serie || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Processador</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.processador || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Memória RAM</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.memoria_ram || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Armazenamento</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.armazenamento || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Placa de Vídeo</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.placa_video || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Sistema Operacional</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {computador.sistema_operacional || 'Não informado'}
                </Typography>
              </Paper>
            </Grid>
            {computador.observacoes && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Observações</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {computador.observacoes}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>

      {/* Peças Mais Trocadas */}
      {estatisticas.pecas_mais_trocadas.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
              🔧 Peças Mais Trocadas
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {estatisticas.pecas_mais_trocadas.map((peca, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#0A2647' }}>
                      {peca.descricao}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trocada {peca.quantidade}x
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      )}

      {/* Serviços Mais Realizados */}
      {estatisticas.servicos_mais_realizados.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
              🛠️ Serviços Mais Realizados
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {estatisticas.servicos_mais_realizados.map((servico, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#0A2647' }}>
                      {servico.servico}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Realizado {servico.quantidade}x
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      )}

      {/* Técnicos que Mexeram */}
      {estatisticas.tecnicos_que_mexeram.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
              👨‍🔧 Técnicos que Já Mexeram
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {estatisticas.tecnicos_que_mexeram.map((tecnico, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#0A2647' }}>
                      {tecnico.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Atendeu {tecnico.total}x
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      )}

      {/* Histórico de Manutenções */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
            📋 Histórico de Manutenções
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          {historico.length === 0 ? (
            <Typography color="text.secondary">Nenhuma manutenção registrada</Typography>
          ) : (
            historico.map((os, index) => {
              const servicos = safeJsonParse(os.servicos);
              const pecas = safeJsonParse(os.pecas);

              return (
                <Accordion key={os.id} sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%', flexWrap: 'wrap' }}>
                      <Chip
                        label={`OS #${os.numero_os}`}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={os.status}
                        size="small"
                        color={getStatusColor(os.status)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(os.data_abertura)}
                      </Typography>
                      {os.tecnico_nome && (
                        <Typography variant="caption" color="text.secondary">
                          Técnico: {os.tecnico_nome}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600 }}>
                        {formatCurrency(os.valor_total)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {os.relato_cliente && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Relato do Cliente</Typography>
                          <Typography variant="body2">{os.relato_cliente}</Typography>
                        </Grid>
                      )}
                      {servicos.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Serviços</Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {servicos.map((s, i) => (
                              <Chip key={i} label={s} size="small" color="primary" variant="outlined" />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {pecas.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Peças</Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Descrição</TableCell>
                                  <TableCell align="center">Qtd</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {pecas.map((p, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{p.descricao || p.nome || 'Peça'}</TableCell>
                                    <TableCell align="center">{p.quantidade || 1}</TableCell>
                                    <TableCell align="right">{formatCurrency(p.valor_total || 0)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                      {os.observacoes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Observações</Typography>
                          <Typography variant="body2">{os.observacoes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Card>
    </Layout>
  );
}

export default FichaTecnica;
